import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Paper,
  Alert,
  Divider,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  InputAdornment,
  CircularProgress,
  Avatar,
  Autocomplete,
} from '@mui/material';
import {
  CloudUpload,
  Receipt,
  CheckCircle,
  Cancel,
  Edit,
  Save,
  CalendarToday,
  AccountBalance,
  AttachMoney,
  Business,
  Person,
  Description,
  DateRange,
  Image as ImageIcon,
  Delete,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { INCOMING_PAYMENT_APPROVED_EVENT } from '../../utils/eventBus';
import CreditService from '../../services/creditService';
import UserService from '../../services/userService';
import AuthService from '../../services/authService';
import useResolvedUserId from '../../hooks/useResolvedUserId';

const InvoiceApproval = ({ user }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const currentUserId = useResolvedUserId(user);
  
  // State for invoice image
  const [invoiceImage, setInvoiceImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  
  // State for invoice data (read from OCR)
  const [invoiceData, setInvoiceData] = useState({
    invoiceNumber: '',
    invoiceDate: '',
    sellerName: '',
    sellerTaxNumber: '',
    sellerId: '', // Seller UserId - required for payment creation
    buyerName: '',
    buyerTaxNumber: '',
    amount: '',
    currency: 'EUR',
    description: '',
    paymentDate: '',
  });
  
  // State for editing
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState(invoiceData);
  
  // State for approval
  const [approvalDialog, setApprovalDialog] = useState({ open: false, action: null });
  const [loading, setLoading] = useState(false);
  
  // State for seller selection (like New Payment page)
  const [previousSellers, setPreviousSellers] = useState([]);
  const [loadingSellers, setLoadingSellers] = useState(false);
  const [sellerInputValue, setSellerInputValue] = useState('');
  
  // Mock limit data
  const [limitInfo] = useState({
    totalLimit: 100000,
    usedLimit: 35000,
    availableLimit: 65000,
    currency: 'EUR',
  });

  const isPdfOrXml = (file) => {
    if (!file?.type) return false;
    const t = file.type.toLowerCase();
    const name = (file.name || '').toLowerCase();
    return t === 'application/pdf' || t === 'application/xml' || t === 'text/xml' ||
           name.endsWith('.pdf') || name.endsWith('.xml') || name.endsWith('.ubl');
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        setInvoiceImage(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
          simulateOCRReading(file);
        };
        reader.readAsDataURL(file);
      } else if (isPdfOrXml(file)) {
        setInvoiceImage(file);
        setImagePreview(null);
        analyzeEInvoice(file);
      } else {
        toast.error('Please select a valid file (PNG, JPG, PDF or XML/UBL)');
      }
    }
  };

  // OCR reading via API
  const simulateOCRReading = async (file) => {
    setLoading(true);
    try {
      // Create FormData to send file
      const formData = new FormData();
      formData.append('file', file);

      // Get API base URL and auth token
      const apiBase = window.APP_CONFIG?.apiUrl || 'https://api.dpg.net.tr';
      const token = AuthService.getAuthToken();
      
      // Make API call to backend proxy endpoint
      const response = await fetch(`${apiBase}/api/invoice/analyze-e-invoice`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          // Don't set Content-Type - browser will set it with boundary for FormData
        },
        body: formData,
      });

      console.log('=== API Response Status ===');
      console.log('Status:', response.status);
      console.log('Status Text:', response.statusText);
      console.log('OK:', response.ok);
      console.log('===========================');

      if (!response.ok) {
        const errorText = await response.text();
        console.error('=== API Error Response ===');
        console.error('Status:', response.status);
        console.error('Error Text:', errorText);
        console.error('===========================');
        
        // Try to parse error as JSON
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorMessage;
        } catch (e) {
          // Not JSON, use text as is
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      // Log detailed API response
      console.log('=== API Response Details ===');
      console.log('Full Response:', result);
      console.log('Success:', result.success);
      console.log('Error:', result.error);
      console.log('Invoice Number:', result.invoice_number);
      console.log('Issue Date:', result.issue_date);
      console.log('Supplier Name:', result.supplier_name);
      console.log('Supplier VKN:', result.supplier_vkn);
      console.log('Customer Name:', result.customer_name);
      console.log('Customer VKN:', result.customer_vkn);
      console.log('Grand Total:', result.grand_total);
      console.log('Currency:', result.currency);
      console.log('Line Items:', result.line_items);
      console.log('===========================');

      if (result.success) {
        // Get buyer tax number from user profile (fallback)
        let buyerTaxNumber = '';
        try {
          const profileResult = await UserService.getCurrentUserProfile();
          if (profileResult.success && profileResult.data) {
            const profile = profileResult.data.data || profileResult.data;
            buyerTaxNumber = profile.taxNumber || '';
          }
        } catch (error) {
          console.error('Error loading user profile for tax number:', error);
        }

        // Handle description from line items (like analyzeEInvoice)
        const descriptionFromLines = result.line_items?.length
          ? result.line_items.map((li) => li.description || li.code || '').filter(Boolean).join('; ')
          : '';

        // Map API response to invoice data format (e-fatura format)
        const mappedData = {
          invoiceNumber: result.invoice_number || '',
          invoiceDate: result.issue_date || '',
          sellerName: result.supplier_name || '',
          sellerTaxNumber: result.supplier_vkn || '',
          sellerId: '', // Seller UserId - will be filled manually or found by tax number
          buyerName: result.customer_name || (user?.name && user?.surname ? `${user.name} ${user.surname}` : (user?.name || '')),
          buyerTaxNumber: result.customer_vkn || buyerTaxNumber, // Use API value first, fallback to profile
          amount: result.grand_total != null ? result.grand_total.toString() : '',
          currency: result.currency || 'EUR',
          description: descriptionFromLines || result.payment_means || '',
          paymentDate: result.due_date || '',
        };

        console.log('=== Mapped Data ===');
        console.log('Mapped Invoice Data:', mappedData);
        console.log('===================');

        setInvoiceData(mappedData);
        setEditedData(mappedData);
        // Automatically enable edit mode when invoice is read
        setIsEditing(true);
        toast.success('Invoice read successfully. You can now edit the information.');
      } else {
        console.error('=== API Returned Error ===');
        console.error('Success:', result.success);
        console.error('Error Message:', result.error);
        console.error('Full Result:', result);
        console.error('===========================');
        throw new Error(result.error || 'Failed to read invoice');
      }
    } catch (error) {
      console.error('=== Exception Caught ===');
      console.error('Error Name:', error.name);
      console.error('Error Message:', error.message);
      console.error('Error Stack:', error.stack);
      console.error('Full Error Object:', error);
      console.error('========================');
      toast.error(error.message || 'Failed to read invoice. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const analyzeEInvoice = async (file) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const apiBase = window.APP_CONFIG?.apiUrl || 'https://api.dpg.net.tr';
      const token = AuthService.getAuthToken();

      const response = await fetch(`${apiBase}/api/invoice/analyze-e-invoice`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorMessage;
        } catch (e) {}
        throw new Error(errorMessage);
      }

      const result = await response.json();

      if (result.success) {
        let buyerTaxNumber = '';
        try {
          const profileResult = await UserService.getCurrentUserProfile();
          if (profileResult.success && profileResult.data) {
            const profile = profileResult.data.data || profileResult.data;
            buyerTaxNumber = profile.taxNumber || '';
          }
        } catch (error) {
          console.error('Error loading user profile for tax number:', error);
        }

        const descriptionFromLines = result.line_items?.length
          ? result.line_items.map((li) => li.description || li.code || '').filter(Boolean).join('; ')
          : '';

        const mappedData = {
          invoiceNumber: result.invoice_number || '',
          invoiceDate: result.issue_date || '',
          sellerName: result.supplier_name || '',
          sellerTaxNumber: result.supplier_vkn || '',
          sellerId: '',
          buyerName: result.customer_name || (user?.name && user?.surname ? `${user.name} ${user.surname}` : (user?.name || '')),
          buyerTaxNumber: result.customer_vkn || buyerTaxNumber,
          amount: result.grand_total != null ? result.grand_total.toString() : '',
          currency: result.currency || 'EUR',
          description: descriptionFromLines || '',
          paymentDate: result.due_date || '',
        };

        setInvoiceData(mappedData);
        setEditedData(mappedData);
        setIsEditing(true);
        toast.success('Invoice read successfully. You can now edit the information.');
      } else {
        throw new Error(result.error || 'Failed to read invoice');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to read invoice. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch all active non-admin users (sellers) - like New Payment page
  const fetchPreviousSellers = useCallback(async () => {
    if (!currentUserId) return;
    setLoadingSellers(true);
    try {
      const result = await UserService.getActiveNonAdminUsers();
      if (result.success) {
        // Filter out current user (buyer cannot be seller)
        const sellers = (result.data || []).filter(seller => seller.sellerId !== currentUserId);
        setPreviousSellers(sellers);
      }
    } catch (error) {
      console.error('Error fetching active sellers:', error);
    } finally {
      setLoadingSellers(false);
    }
  }, [currentUserId]);

  // Get seller name and tax number by ID
  const fetchSellerInfo = async (sellerId) => {
    if (!sellerId) {
      setEditedData(prev => ({ ...prev, sellerName: '', sellerTaxNumber: '' }));
      return;
    }

    // Check if user is trying to enter their own ID
    if (sellerId === currentUserId?.toString()) {
      toast.error('You cannot create a payment for yourself. Please enter a different seller ID.');
      setEditedData(prev => ({ ...prev, sellerName: '', sellerId: '', sellerTaxNumber: '' }));
      return;
    }

    try {
      // Get seller name
      const nameResult = await UserService.getCustomerNameByUserId(sellerId);
      if (nameResult.success) {
        setEditedData(prev => ({ 
          ...prev, 
          sellerName: nameResult.data.customerName
        }));
        
        // Try to get seller profile for tax number (may fail if user is not admin)
        try {
          const profileResult = await UserService.getUserDetailsByAdmin(sellerId);
          if (profileResult.success && profileResult.data?.taxNumber) {
            setEditedData(prev => ({
              ...prev,
              sellerTaxNumber: profileResult.data.taxNumber
            }));
          }
        } catch (error) {
          // Silently fail - tax number will remain empty, user can fill manually
          console.log('Could not fetch seller tax number (may require admin access)');
        }
      } else {
        setEditedData(prev => ({ ...prev, sellerName: '', sellerTaxNumber: '' }));
        toast.error(nameResult.message || 'Seller not found');
      }
    } catch (error) {
      console.error('Error fetching seller info:', error);
      setEditedData(prev => ({ ...prev, sellerName: '', sellerTaxNumber: '' }));
    }
  };

  // Handle seller selection from autocomplete
  const handleSellerChange = async (event, newValue, reason) => {
    if (reason === 'selectOption' && newValue && typeof newValue === 'object') {
      // Selected from dropdown - set both sellerId and sellerName immediately
      const sellerIdStr = newValue.sellerId.toString();
      setEditedData(prev => ({
        ...prev,
        sellerId: sellerIdStr,
        sellerName: newValue.sellerName
      }));
      // Update inputValue to show the selected seller name (not ID)
      setSellerInputValue(newValue.sellerName);
      
      // Try to fetch seller tax number (may fail if user is not admin)
      try {
        const profileResult = await UserService.getUserDetailsByAdmin(sellerIdStr);
        if (profileResult.success && profileResult.data?.taxNumber) {
          setEditedData(prev => ({
            ...prev,
            sellerTaxNumber: profileResult.data.taxNumber
          }));
        }
      } catch (error) {
        // Silently fail - tax number will remain empty, user can fill manually
        console.log('Could not fetch seller tax number (may require admin access)');
      }
    } else if (reason === 'clear') {
      // Cleared
      setEditedData(prev => ({
        ...prev,
        sellerId: '',
        sellerName: '',
        sellerTaxNumber: ''
      }));
      setSellerInputValue('');
    }
  };

  // Debounce seller ID fetch when user types (only when manually typing, not when selecting from dropdown)
  useEffect(() => {
    // Skip if sellerId is already set and matches the input (to avoid conflicts with handleSellerChange)
    if (editedData.sellerId && sellerInputValue === editedData.sellerId.toString()) {
      return;
    }

    if (!sellerInputValue || sellerInputValue.trim() === '') {
      // Only clear if sellerId is also empty
      if (!editedData.sellerId) {
        setEditedData(prev => ({ ...prev, sellerId: '', sellerName: '' }));
      }
      return;
    }

    // If it's a number (likely ID)
    if (/^\d+$/.test(sellerInputValue.trim())) {
      const trimmedValue = sellerInputValue.trim();
      
      // Check if this ID is in previousSellers
      const sellerFromList = previousSellers.find(s => s.sellerId.toString() === trimmedValue);
      
      if (sellerFromList) {
        setEditedData(prev => ({
          ...prev,
          sellerId: trimmedValue,
          sellerName: sellerFromList.sellerName
        }));
        return;
      }
      
      // Not in previousSellers, update sellerId and fetch from API
      setEditedData(prev => ({ ...prev, sellerId: trimmedValue }));
      const timeoutId = setTimeout(() => {
        fetchSellerInfo(trimmedValue);
      }, 500);
      return () => clearTimeout(timeoutId);
    } else {
      // If it's text, try to find in previous sellers
      const found = previousSellers.find(s => 
        s.sellerName.toLowerCase() === sellerInputValue.toLowerCase()
      );
      if (found) {
        setEditedData(prev => ({
          ...prev,
          sellerId: found.sellerId.toString(),
          sellerName: found.sellerName
        }));
      } else {
        setEditedData(prev => ({ ...prev, sellerId: sellerInputValue, sellerName: '' }));
      }
    }
  }, [sellerInputValue, previousSellers, currentUserId]);

  // Fetch sellers on component mount
  useEffect(() => {
    fetchPreviousSellers();
  }, [fetchPreviousSellers]);

  // Handle edit mode
  const handleEdit = () => {
    setIsEditing(true);
    setEditedData({ ...invoiceData });
  };

  // Handle save edits
  const handleSaveEdit = () => {
    setInvoiceData(editedData);
    setIsEditing(false);
    toast.success('Invoice information updated');
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditedData(invoiceData);
    setIsEditing(false);
  };

  // Handle field change
  const handleFieldChange = (field, value) => {
    setEditedData({
      ...editedData,
      [field]: value,
    });
  };

  // Handle approval/rejection
  const handleApprovalAction = (action) => {
    setApprovalDialog({ open: true, action });
  };

  // Confirm approval/rejection
  const confirmApprovalAction = async () => {
    const { action } = approvalDialog;
    
    if (action === 'approve') {
      setLoading(true);
      setApprovalDialog({ open: false, action: null });
      
      try {
        // Get user IBAN from profile
        let buyerIban = '';
        try {
          const profileResult = await UserService.getCurrentUserProfile();
          if (profileResult.success && profileResult.data) {
            const profile = profileResult.data.data || profileResult.data;
            buyerIban = profile.ibanNumber || '';
            if (!buyerIban) {
              toast.error('Please add your IBAN from the Profile page before creating a payment.');
              setLoading(false);
              return;
            }
            // Remove spaces and convert to uppercase
            buyerIban = buyerIban.replace(/\s/g, '').trim().toUpperCase();
          } else {
            toast.error('Please add your IBAN from the Profile page before creating a payment.');
            setLoading(false);
            return;
          }
        } catch (error) {
          console.error('Error loading user profile:', error);
          toast.error('Please add your IBAN from the Profile page before creating a payment.');
          setLoading(false);
          return;
        }

        // Validate required fields
        if (!editedData.sellerId) {
          toast.error('Seller ID is required. Please enter the seller User ID.');
          setLoading(false);
          return;
        }

        if (!editedData.sellerName) {
          toast.error('Seller name is required.');
          setLoading(false);
          return;
        }

        // Check if user is trying to create payment for themselves
        const sellerIdNum = parseInt(editedData.sellerId);
        if (sellerIdNum === currentUserId) {
          toast.error('You cannot create a payment for yourself. Please enter a different seller ID.');
          setLoading(false);
          return;
        }

        if (!editedData.amount || parseFloat(editedData.amount) <= 0) {
          toast.error('Amount must be greater than zero.');
          setLoading(false);
          return;
        }

        // Calculate validity date - must be in the future
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        let validityDate = null;
        
        if (editedData.paymentDate) {
          const paymentDate = new Date(editedData.paymentDate);
          paymentDate.setHours(0, 0, 0, 0);
          // If payment date is in the future, use it; otherwise use tomorrow
          if (paymentDate > today) {
            const year = paymentDate.getFullYear();
            const month = String(paymentDate.getMonth() + 1).padStart(2, '0');
            const day = String(paymentDate.getDate()).padStart(2, '0');
            validityDate = `${year}-${month}-${day}`;
          } else {
            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1);
            const year = tomorrow.getFullYear();
            const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
            const day = String(tomorrow.getDate()).padStart(2, '0');
            validityDate = `${year}-${month}-${day}`;
          }
        } else {
          // If no payment date, use tomorrow
          const tomorrow = new Date(today);
          tomorrow.setDate(tomorrow.getDate() + 1);
          const year = tomorrow.getFullYear();
          const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
          const day = String(tomorrow.getDate()).padStart(2, '0');
          validityDate = `${year}-${month}-${day}`;
        }

        // Currency mapping (1=TRY, 2=USD, 3=EUR, 4=GBP)
        const currencyMap = {
          'TRY': 1,
          'USD': 2,
          'EUR': 3,
          'GBP': 4
        };
        const currencyCode = (editedData.currency || 'EUR').toUpperCase();
        const currencyId = currencyMap[currencyCode] || 3; // Default to EUR

        // Prepare credit data
        const creditData = {
          sellerId: parseInt(editedData.sellerId),
          sellerName: editedData.sellerName,
          amount: parseFloat(editedData.amount),
          currency: currencyId,
          validityDate: validityDate,
          description: editedData.description || 'Invoice Payment',
          buyerIban: buyerIban
        };

        console.log('[InvoiceApproval] Creating payment with data:', creditData);

        // Create payment via API
        const result = await CreditService.createCreditRequest(creditData);
        
        if (result.success) {
          const paymentNumber = result.data?.paymentNumber;
          const isGuaranteed = result.data?.isGuaranteed || false;
          
          // Save invoice image to localStorage with payment number
          if (imagePreview && paymentNumber) {
            const invoiceImages = JSON.parse(localStorage.getItem('invoiceImages') || '{}');
            invoiceImages[paymentNumber] = imagePreview;
            localStorage.setItem('invoiceImages', JSON.stringify(invoiceImages));
            console.log('[InvoiceApproval] Invoice image saved for payment:', paymentNumber);
          }

          const paymentType = isGuaranteed ? 'Payment Guaranteed' : 'Payment';
          const successMessage = paymentNumber 
            ? `Payment created successfully! ${paymentType} Number: ${paymentNumber}`
            : 'Payment created successfully!';
          
          toast.success(successMessage);
          
          // Navigate to Payments page
          setTimeout(() => {
            navigate('/user/credits');
          }, 1500);
        } else {
          // Error is already shown by CreditService
          console.error('[InvoiceApproval] Payment creation failed:', result);
        }
      } catch (error) {
        console.error('[InvoiceApproval] Exception in confirmApprovalAction:', error);
        toast.error('An error occurred while creating the payment. Please try again.');
      } finally {
        setLoading(false);
      }
    } else {
      // Reject action
      setApprovalDialog({ open: false, action: null });
      toast.info('Invoice rejected.');
    }
  };

  // Remove image
  const handleRemoveImage = () => {
    setInvoiceImage(null);
    setImagePreview(null);
    setInvoiceData({
      invoiceNumber: '',
      invoiceDate: '',
      sellerName: '',
      sellerTaxNumber: '',
      sellerId: '',
      buyerName: '',
      buyerTaxNumber: '',
      amount: '',
      currency: 'EUR',
      description: '',
      paymentDate: '',
    });
    setEditedData({
      invoiceNumber: '',
      invoiceDate: '',
      sellerName: '',
      sellerTaxNumber: '',
      sellerId: '',
      buyerName: '',
      buyerTaxNumber: '',
      amount: '',
      currency: 'EUR',
      description: '',
      paymentDate: '',
    });
  };

  // Format currency
  const formatCurrency = (amount, currency = 'EUR') => {
    if (!amount) return '0.00';
    const formatter = new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return formatter.format(parseFloat(amount));
  };

  // Limit check removed - backend will determine if payment is guaranteed

  const currentData = isEditing ? editedData : invoiceData;
  // Check if we have invoice data (amount or seller name indicates data was loaded)
  const hasInvoiceData = invoiceData.amount !== '' || invoiceData.sellerName !== '';

  return (
    <Box sx={{ minHeight: '100vh', py: 4, px: { xs: 2, sm: 3 } }}>
      <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
        
        {/* Main Card */}
        <Card elevation={24} sx={{ 
          borderRadius: 4,
          overflow: 'hidden'
        }}>
          
          {/* Header Banner */}
          <Box sx={{ 
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
            p: 3,
            color: 'white'
          }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Avatar sx={{ 
                  width: 64, 
                  height: 64,
                  background: 'rgba(255,255,255,0.25)',
                  border: '3px solid rgba(255,255,255,0.3)'
                }}>
                  <Receipt sx={{ fontSize: '1.5rem' }} />
                </Avatar>
              </Grid>
              <Grid item xs>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Upload New Invoice
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Upload invoice documents and review information before approval.
                </Typography>
              </Grid>
              <Grid item xs={12} sm="auto">
                <Grid container spacing={2}>
                  <Grid item xs={4} sm="auto">
                    <Box sx={{ textAlign: { xs: 'center', sm: 'right' } }}>
                      <Typography variant="caption" sx={{ opacity: 0.9, display: 'block' }}>
                        Total Limit
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {formatCurrency(limitInfo.totalLimit, limitInfo.currency)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4} sm="auto">
                    <Box sx={{ textAlign: { xs: 'center', sm: 'right' } }}>
                      <Typography variant="caption" sx={{ opacity: 0.9, display: 'block' }}>
                        Used Limit
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {formatCurrency(limitInfo.usedLimit, limitInfo.currency)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4} sm="auto">
                    <Box sx={{ textAlign: { xs: 'center', sm: 'right' } }}>
                      <Typography variant="caption" sx={{ opacity: 0.9, display: 'block' }}>
                        Available Limit
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {formatCurrency(limitInfo.availableLimit, limitInfo.currency)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>

          {/* Content */}
          <CardContent sx={{ p: 4 }}>
            
            {/* Invoice Image */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <ImageIcon sx={{ color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="600">
                  Invoice Image
                </Typography>
              </Box>

            {!invoiceImage ? (
              <Paper
                sx={{
                  p: 4,
                  border: '2px dashed',
                  borderColor: 'primary.main',
                  borderRadius: 3,
                  textAlign: 'center',
                  bgcolor: 'grey.50',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'grey.100',
                    borderColor: 'primary.dark',
                  },
                }}
                onClick={() => document.getElementById('invoice-upload').click()}
              >
                <input
                  id="invoice-upload"
                  type="file"
                  accept="image/*,.pdf,application/pdf,.xml,application/xml,text/xml,.ubl"
                  style={{ display: 'none' }}
                  onChange={handleImageUpload}
                />
                <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" color="primary" gutterBottom>
                  Upload Invoice
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  PNG, JPG, PDF or XML (UBL)
                </Typography>
              </Paper>
            ) : (
              <Box>
                <Box sx={{ position: 'relative', mb: 2 }}>
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Invoice"
                      style={{
                        width: '100%',
                        height: 'auto',
                        borderRadius: 8,
                        border: '1px solid #e0e0e0',
                      }}
                    />
                  ) : (
                    <Paper
                      sx={{
                        p: 3,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'grey.50',
                      }}
                    >
                      <Receipt sx={{ fontSize: 48, color: 'primary.main' }} />
                      <Box>
                        <Typography variant="subtitle1" fontWeight="600">
                          {invoiceImage.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          PDF or XML — sent for analysis
                        </Typography>
                      </Box>
                    </Paper>
                  )}
                  <IconButton
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: 'error.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'error.dark' },
                    }}
                    onClick={handleRemoveImage}
                    size="small"
                  >
                    <Delete />
                  </IconButton>
                </Box>
                {loading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ ml: 2 }}>
                      Reading invoice...
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            </Box>

            {/* Invoice Information */}
            <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Receipt sx={{ color: 'primary.main' }} />
                    <Typography variant="h6" fontWeight="600">
                      Invoice Information
                    </Typography>
                  </Box>
                  {hasInvoiceData && isEditing && (
                    <Typography variant="body2" color="text.secondary">
                      You can edit the information before approving
                    </Typography>
                  )}
                </Box>

                {!hasInvoiceData ? (
                  <Alert severity="info">
                    Upload and read the invoice image.
                  </Alert>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Invoice Number and Date - Side by side */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField
                        fullWidth
                        label="Invoice Number"
                        value={currentData.invoiceNumber}
                        onChange={(e) => handleFieldChange('invoiceNumber', e.target.value)}
                        disabled={!isEditing}
                        variant="outlined"
                        size="small"
                      />
                      <TextField
                        fullWidth
                        label="Invoice Date"
                        type="date"
                        value={currentData.invoiceDate}
                        onChange={(e) => handleFieldChange('invoiceDate', e.target.value)}
                        disabled={!isEditing}
                        variant="outlined"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Box>

                    {/* Seller Information */}
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Seller Information
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Autocomplete
                        disabled={!isEditing}
                        options={previousSellers}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') return option;
                          return option.sellerName; // Show only seller name in input
                        }}
                        value={previousSellers.find(s => s.sellerId.toString() === editedData.sellerId) || null}
                        onChange={handleSellerChange}
                        inputValue={sellerInputValue}
                        onInputChange={(event, newInputValue, reason) => {
                          // Only update inputValue if it's user input, not when selecting from dropdown
                          if (reason === 'input') {
                            setSellerInputValue(newInputValue);
                          }
                        }}
                        loading={loadingSellers}
                        filterOptions={(options, { inputValue }) => {
                          if (!inputValue) return options;
                          const searchTerm = inputValue.toLowerCase();
                          return options.filter(option =>
                            option.sellerName.toLowerCase().includes(searchTerm) ||
                            option.sellerId.toString().includes(searchTerm)
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Seller ID or Name"
                            placeholder="Enter seller ID or select from list"
                            required
                            fullWidth
                            error={
                              (currentData.sellerId === currentUserId?.toString()) && 
                              currentData.sellerId !== ''
                            }
                            helperText={
                              (currentData.sellerId === currentUserId?.toString()) && 
                              currentData.sellerId !== ''
                                ? 'You cannot create a payment for yourself'
                                : ''
                            }
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <>
                                  <InputAdornment position="start">
                                    <Business color={currentData.sellerId ? 'primary' : 'disabled'} />
                                  </InputAdornment>
                                  {params.InputProps.startAdornment}
                                </>
                              ),
                              endAdornment: (
                                <>
                                  {loadingSellers ? <CircularProgress color="inherit" size={20} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                            size="small"
                          />
                        )}
                        renderOption={(props, option) => {
                          const { key, ...otherProps } = props;
                          return (
                            <Box component="li" key={key} {...otherProps}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                <Business color="primary" fontSize="small" />
                                <Typography variant="body1" fontWeight={600}>
                                  {option.sellerName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                                  ID: {option.sellerId}
                                </Typography>
                              </Box>
                            </Box>
                          );
                        }}
                      />
                      <TextField
                        fullWidth
                        label="Seller Tax Number"
                        value={currentData.sellerTaxNumber}
                        onChange={(e) => handleFieldChange('sellerTaxNumber', e.target.value)}
                        disabled={!isEditing}
                        variant="outlined"
                        size="small"
                      />
                    </Box>

                    {/* Buyer Information */}
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Buyer Information
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        fullWidth
                        label="Buyer Name"
                        value={currentData.buyerName}
                        onChange={(e) => handleFieldChange('buyerName', e.target.value)}
                        disabled={!isEditing}
                        variant="outlined"
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Person fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <TextField
                        fullWidth
                        label="Buyer Tax Number"
                        value={currentData.buyerTaxNumber}
                        onChange={(e) => handleFieldChange('buyerTaxNumber', e.target.value)}
                        disabled={!isEditing}
                        variant="outlined"
                        size="small"
                      />
                    </Box>

                    {/* Other Information */}
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField
                        fullWidth
                        label="Amount"
                        value={currentData.amount}
                        onChange={(e) => handleFieldChange('amount', e.target.value)}
                        disabled={!isEditing}
                        variant="outlined"
                        size="small"
                        type="number"
                        InputProps={{
                          
                          endAdornment: (
                            <InputAdornment position="end">
                              <Chip label={currentData.currency} size="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <TextField
                        fullWidth
                        label="Payment Date"
                        type="date"
                        value={currentData.paymentDate}
                        onChange={(e) => handleFieldChange('paymentDate', e.target.value)}
                        disabled={!isEditing}
                        variant="outlined"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarToday fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                    <TextField
                      fullWidth
                      label="Description"
                      value={currentData.description}
                      onChange={(e) => handleFieldChange('description', e.target.value)}
                      disabled={!isEditing}
                      variant="outlined"
                      size="small"
                      multiline
                      rows={3}
                      inputProps={{
                        maxLength: 200
                      }}
                      helperText={`${(currentData.description || '').length}/200 characters`}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                            <Description fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    {hasInvoiceData && (
                      <Box>
                        <Divider sx={{ my: 2 }} />
                        <Alert severity="info" sx={{ mt: 2 }}>
                          Payment will be created. If you have sufficient limit, it will be guaranteed by the bank. Otherwise, it will be created without guarantee.
                        </Alert>
                      </Box>
                    )}
                  </Box>
                )}
            </Box>

            {/* Action Buttons */}
            {hasInvoiceData && (
              <Box sx={{ mt: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 2 }}>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Cancel />}
                    onClick={() => handleApprovalAction('reject')}
                    size="large"
                    sx={{ minWidth: 150 }}
                    disabled={loading}
                  >
                    Reject
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircle />}
                    onClick={() => handleApprovalAction('approve')}
                    size="large"
                    sx={{ minWidth: 150 }}
                    disabled={loading}
                  >
                    Approve
                  </Button>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Approval Dialog */}
        <Dialog
          open={approvalDialog.open}
          onClose={() => setApprovalDialog({ open: false, action: null })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {approvalDialog.action === 'approve' ? 'Approve Invoice' : 'Reject Invoice'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {approvalDialog.action === 'approve' ? (
                <>
                  Are you sure you want to approve this invoice?
                  <br />
                  <br />
                  <strong>When approved:</strong>
                  <ul>
                    <li>Payment will be created</li>
                    <li>If you have sufficient limit, payment will be guaranteed by the bank</li>
                    <li>If limit is insufficient, payment will be created without guarantee</li>
                    <li>Payment date will be recorded</li>
                  </ul>
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="body2">
                      <strong>Invoice Amount:</strong> {formatCurrency(editedData.amount, editedData.currency)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Available Limit:</strong> {formatCurrency(limitInfo.availableLimit, limitInfo.currency)}
                    </Typography>
                  </Box>
                </>
              ) : (
                'Are you sure you want to reject this invoice?'
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setApprovalDialog({ open: false, action: null })}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmApprovalAction}
              color={approvalDialog.action === 'approve' ? 'success' : 'error'}
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? 'Processing...' : approvalDialog.action === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </Box>
  );
};

export default InvoiceApproval;

